import React, { useEffect, useState } from "react";
import axios from 'axios';
import {
  TrendingUp,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Scale,
  PlusCircle,
  X,
  CreditCard,
  ArrowLeftRight,
  User,
  Tag,
  Calendar,
  FileText,
  Save,
  Plus,
  List,
  RefreshCw,
  Table as TableIcon,
  Hash,
  Edit
} from 'lucide-react';
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import {
  PageLayout,
  PageHeader,
  Button,
  Card,
  StatCard,
  StatCardGrid,
  Table,
  Badge,
  Modal,
  Spinner
} from '../components/ui';
import FormInput from '../components/ui/FormInput';

const BalanceBook = ({ onBack }) => {
  const { permissions, roleName, data } = useAuth();
  const userRole = roleName === 'superadmin' ? 'superadmin' : 'other';
  const loginId = data?.id;
  const branchId = data?.branchId;
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [baseGroups, setBaseGroups] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);
  const [dayEndData, setDayEndData] = useState(null);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ officer_Id: loginId });
  const [transactionFilter, setTransactionFilter] = useState('all');

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  useEffect(() => {
    loadDayEndData();
    loadLedgerData();
    loadAccountsForDropdown();
    loadStaffDetails();
    loadBaseGroupsForDropdown();
  }, []);

  const loadDayEndData = async () => {
    try {
      const yesterday = getYesterdayDate();
      const filter = {
        date: yesterday,
        ...(userRole !== 'superadmin' && branchId ? { branchId } : {})
      };
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/DayEndProcess`, {
        params: {
          includeDepth: 1,
          filter: JSON.stringify(filter)
        }
      });
      const dayEndArray = Array.isArray(response.data?.data) ? response.data.data : [];
      const yesterdayData = dayEndArray.find(item => item.date === yesterday);
      setDayEndData(yesterdayData);
      setOpeningBalance(yesterdayData?.outstanding ? parseFloat(yesterdayData.outstanding) : 0);
    } catch (error) {
      console.error('Error loading day end data:', error);
      setDayEndData(null);
      setOpeningBalance(0);
    }
  };

  const loadBaseGroupsForDropdown = async () => {
    try {
      const response = await axios.get('/api/getModelData/base_ledger_group', {
        baseURL: API_BASE_URL
      });
      const groupsArray = Array.isArray(response.data?.data) ? response.data.data : [];
      setBaseGroups(groupsArray);
    } catch (error) {
      console.error('Error loading base groups for dropdown:', error);
      setBaseGroups([]);
    }
  };

  const loadAccountsForDropdown = async () => {
    try {
      const response = await axios.get('/api/getModelData/ledger_account?includeDepth=1', {
        baseURL: API_BASE_URL
      });
      const accountsArray = Array.isArray(response.data?.data) ? response.data.data : [];
      setLedgerAccounts(accountsArray);
    } catch (error) {
      console.error('Error loading accounts for dropdown:', error);
      setLedgerAccounts([]);
    }
  };

  const loadStaffDetails = async () => {
    try {
      const response = await axios.get('/api/getModelData/staff_details', {
        baseURL: API_BASE_URL
      });
      const staffArray = Array.isArray(response.data?.data) ? response.data.data : [];
      setStaffDetails(staffArray);
    } catch (error) {
      console.error('Error loading staff details:', error);
      setStaffDetails([]);
    }
  };

  const loadLedgerData = async () => {
    setLoading(true);
    try {
      const filter = {
        date: getTodayDate(),
        ...(userRole !== 'superadmin' && branchId ? { branchId } : {})
      };
      const response = await axios.get('/api/getModelData/ledger_transaction', {
        baseURL: API_BASE_URL,
        params: {
          filter: JSON.stringify(filter)
        }
      });
      const transactions = Array.isArray(response.data?.data) ? response.data.data : [];

      let runningBalance = openingBalance;
      const processedTransactions = transactions
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map(transaction => {
          const amount = transaction.amount / 100;
          if (transaction.transaction_type === 'receipt') {
            runningBalance += amount;
          } else if (transaction.transaction_type === 'payment') {
            runningBalance -= amount;
          }
          return { ...transaction, balance: runningBalance };
        });

      setLedgerTransactions(processedTransactions);
    } catch (error) {
      console.error('Error loading ledger data:', error);
      setLedgerTransactions([]);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        type: editingItem ? 'Edit' : 'Add',
        model: 'ledger_transaction',
        accountId: parseInt(formData.accountId),
        officer_Id: parseInt(formData.officer_Id),
        date: formData.date || getTodayDate(),
        particulars: formData.particulars,
        transaction_type: formData.type,
        amount: parseInt(parseFloat(formData.amount) * 100),
        balanceAfter: 0,
        destination_ledger: formData.destination_ledger ? parseInt(formData.destination_ledger) : null,
        branchId: userRole !== 'superadmin' ? branchId : null
      };

      if (editingItem) {
        requestData.id = editingItem.id;
      }

      await axios.post('/api/addDetails', requestData, {
        baseURL: API_BASE_URL
      });

      await loadLedgerData();
      setShowForm(false);
      setEditingItem(null);
      setFormData({ officer_Id: loginId });

      alert(editingItem ? 'Updated successfully!' : 'Added successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Error saving data. Please try again.');
    }
    setLoading(false);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      officer_Id: loginId,
      amount: item.amount ? (item.amount / 100).toFixed(2) : ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({ officer_Id: loginId });
  };

  const currentAssetsGroup = baseGroups.find(group => group.group_type === 'CurrentAssets');
  const currentAssetsGroupId = currentAssetsGroup ? currentAssetsGroup.id : null;
  const currentAssetsAccounts = ledgerAccounts.filter(account =>
    account.ledger_id === currentAssetsGroupId && account.name && account.id
  );

  const todayBalance = ledgerTransactions.length > 0
    ? ledgerTransactions[ledgerTransactions.length - 1].balance
    : openingBalance;

  const todayPayments = ledgerTransactions
    .filter(t => t.transaction_type === 'payment')
    .reduce((sum, t) => sum + (t.amount / 100), 0);

  const todayReceipts = ledgerTransactions
    .filter(t => t.transaction_type === 'receipt')
    .reduce((sum, t) => sum + (t.amount / 100), 0);

  const filteredTransactions = transactionFilter === 'all'
    ? ledgerTransactions
    : ledgerTransactions.filter(item => item.transaction_type === transactionFilter);

  const selectClass = "bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 w-full px-3.5 py-2.5 text-sm transition-all duration-150 focus:outline-none";

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-6" contentClassName="overflow-visible">
      {onBack && (
        <div className="mb-3">
          <Button variant="secondary" onClick={onBack} size="sm">
            Back
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <StatCardGrid cols={4} className="mb-4">
        <StatCard
          title="Opening Balance"
          value={openingBalance.toFixed(2)}
          prefix="₹ "
          description={`Yesterday's Closing (${getYesterdayDate()})`}
          accent="info"
          accentBar
          icon={<Wallet className="w-4 h-4 text-[#3B82F6]" />}
          className="[&>div:last-child]:!py-2 [&_.mb-2]:!mb-0.5 [&_.mb-2]:!py-0 [&_.font-display]:!text-xl [&>div.absolute]:!p-0 [&>div.absolute]:!w-1"
        />
        <StatCard
          title="Today's Receipts"
          value={todayReceipts.toFixed(2)}
          prefix="₹ "
          description={`${ledgerTransactions.filter(t => t.transaction_type === 'receipt').length} transactions`}
          accent="success"
          accentBar
          icon={<ArrowDownCircle className="w-4 h-4 text-[#10B981]" />}
          className="[&>div:last-child]:!py-2 [&_.mb-2]:!mb-0.5 [&_.mb-2]:!py-0 [&_.font-display]:!text-xl [&>div.absolute]:!p-0 [&>div.absolute]:!w-1"
        />
        <StatCard
          title="Today's Payments"
          value={todayPayments.toFixed(2)}
          prefix="₹ "
          description={`${ledgerTransactions.filter(t => t.transaction_type === 'payment').length} transactions`}
          accent="danger"
          accentBar
          icon={<ArrowUpCircle className="w-4 h-4 text-[#EF4444]" />}
          className="[&>div:last-child]:!py-2 [&_.mb-2]:!mb-0.5 [&_.mb-2]:!py-0 [&_.font-display]:!text-xl [&>div.absolute]:!p-0 [&>div.absolute]:!w-1"
        />
        <StatCard
          title="Current Balance"
          value={todayBalance.toFixed(2)}
          prefix="₹ "
          description={`Live Balance (${getTodayDate()})`}
          accent="primary"
          accentBar
          icon={<Scale className="w-4 h-4 text-[#004BB5]" />}
          className="[&>div:last-child]:!py-2 [&_.mb-2]:!mb-0.5 [&_.mb-2]:!py-0 [&_.font-display]:!text-xl [&>div.absolute]:!p-0 [&>div.absolute]:!w-1"
        />
      </StatCardGrid>

      {/* Controls & Filters row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        {/* Border card containing All Transactions, Payments, Receipts, and Refresh */}
        <Card className="py-2 px-3.5 flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={transactionFilter === 'all' ? 'primary' : 'secondary'}
                onClick={() => setTransactionFilter('all')}
                size="sm"
                className="h-[32px] px-3 text-xs"
              >
                <List size={14} className="mr-1.5" /> All Transactions
              </Button>
              <Button
                variant={transactionFilter === 'payment' ? 'primary' : 'secondary'}
                onClick={() => setTransactionFilter('payment')}
                size="sm"
                className="h-[32px] px-3 text-xs"
              >
                <ArrowUpCircle size={14} className="mr-1.5" /> Payments
              </Button>
              <Button
                variant={transactionFilter === 'receipt' ? 'primary' : 'secondary'}
                onClick={() => setTransactionFilter('receipt')}
                size="sm"
                className="h-[32px] px-3 text-xs"
              >
                <ArrowDownCircle size={14} className="mr-1.5" /> Receipts
              </Button>
            </div>

            <Button
              variant="secondary"
              onClick={loadLedgerData}
              disabled={loading}
              size="sm"
              isLoading={loading}
              className="h-[32px] px-3 text-xs self-start sm:self-auto"
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
        </Card>

        {/* Add Transaction button displayed outside the border on the right side in the same single row */}
        <Button
          variant="primary"
          onClick={() => {
            setEditingItem(null);
            setFormData({ officer_Id: loginId, date: getTodayDate(), type: 'payment' });
            setShowForm(true);
          }}
          size="sm"
          className="h-[48px] px-4 text-xs font-semibold whitespace-nowrap shrink-0 shadow-sm rounded-xl flex items-center justify-center self-stretch sm:self-auto"
          startIcon={<PlusCircle size={15} />}
        >
          Add Transaction
        </Button>
      </div>

      {/* Transactions Table Card */}
      <Card className="p-4">
        <div className="mb-3">
          <h2 className="text-base font-bold text-[#0F172A]">
            {transactionFilter === 'all' ? "Today's All Transactions" :
              transactionFilter === 'payment' ? "Today's Payment Transactions" : "Today's Receipt Transactions"}
          </h2>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-[#64748B]">
            <Spinner size="lg" className="mb-3" />
            <p className="text-sm">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[#E2E8F0] rounded-xl bg-[#F8FAFC]">
            <TableIcon className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
            <h3 className="text-sm font-bold text-[#1E293B] mb-1">No Transactions Found</h3>
            <p className="text-xs text-[#64748B]">
              No {transactionFilter === 'all' ? '' : transactionFilter} transactions found for today.
            </p>
          </div>
        ) : (
          <Table className="min-w-[1400px] max-h-[380px] overflow-x-auto overflow-y-auto">
            <Table.Head>
              <tr>
                <Table.HeadCell><Hash size={12} className="mr-1 inline" />ID</Table.HeadCell>
                <Table.HeadCell><Calendar size={12} className="mr-1 inline" />Date</Table.HeadCell>
                <Table.HeadCell><CreditCard size={12} className="mr-1 inline" />Payment Mode</Table.HeadCell>
                <Table.HeadCell><ArrowLeftRight size={12} className="mr-1 inline" />Destination</Table.HeadCell>
                <Table.HeadCell><User size={12} className="mr-1 inline" />Officer</Table.HeadCell>
                <Table.HeadCell><FileText size={12} className="mr-1 inline" />Particulars</Table.HeadCell>
                <Table.HeadCell align="right"><ArrowUpCircle size={12} className="mr-1 inline" />Payment (₹)</Table.HeadCell>
                <Table.HeadCell align="right"><ArrowDownCircle size={12} className="mr-1 inline" />Receipt (₹)</Table.HeadCell>
                <Table.HeadCell align="right"><Scale size={12} className="mr-1 inline" />Balance (₹)</Table.HeadCell>
                <Table.HeadCell align="center"><Edit size={12} className="mr-1 inline" />Actions</Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {filteredTransactions.map((item, index) => {
                const isPayment = item.transaction_type === 'payment';
                const isReceipt = item.transaction_type === 'receipt';

                return (
                  <Table.Row key={item.id || index}>
                    <Table.Cell className="font-bold text-[#1E293B]">#{item.id}</Table.Cell>
                    <Table.Cell>{new Date(item.date).toLocaleDateString('en-IN')}</Table.Cell>
                    <Table.Cell>
                      <Badge variant="neutral">
                        {ledgerAccounts.find(acc => acc.id === item.accountId)?.name || 'Unknown'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {item.destination_ledger ? (
                        <Badge variant="info">
                          {ledgerAccounts.find(acc => acc.id === item.destination_ledger)?.name || 'Unknown'}
                        </Badge>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                          {staffDetails.find(staff => staff.id === item.officer_Id)?.name?.charAt(0) || 'U'}
                        </span>
                        <span className="text-xs font-medium text-[#475569]">
                          {staffDetails.find(staff => staff.id === item.officer_Id)?.name ||
                            staffDetails.find(staff => staff.id === item.officer_Id)?.username ||
                            'Unknown'}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="inline-block max-w-[180px] truncate" title={item.particulars}>
                        {item.particulars}
                      </span>
                    </Table.Cell>
                    <Table.AmountCell align="right" negative={isPayment}>
                      {isPayment ? (item.amount / 100) : null}
                    </Table.AmountCell>
                    <Table.AmountCell align="right" className={isReceipt ? "text-[#10B981]" : ""}>
                      {isReceipt ? (item.amount / 100) : null}
                    </Table.AmountCell>
                    <Table.AmountCell align="right">
                      {item.balance}
                    </Table.AmountCell>
                    <Table.ActionCell align="center">
                      <button
                        className="p-1.5 text-primary hover:bg-[#E8F0FE] rounded-lg transition-colors duration-150"
                        onClick={() => handleEdit(item)}
                        title="Edit Transaction"
                      >
                        <Edit size={14} />
                      </button>
                    </Table.ActionCell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Card>

      {/* Transaction Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={resetForm}
        title={editingItem ? 'Edit Transaction' : 'Add New Transaction'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={resetForm} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              isLoading={loading}
            >
              {editingItem ? 'Update Transaction' : 'Add Transaction'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#475569] uppercase tracking-wide mb-1">
                Mode of Payment <span className="text-danger-500">*</span>
              </label>
              <select
                className={selectClass}
                value={formData.accountId || ''}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                required
              >
                <option value="">Select Mode of Payment</option>
                {currentAssetsAccounts.length > 0 ? (
                  currentAssetsAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name.toUpperCase()} ({account.account_type ? account.account_type.toUpperCase() : 'N/A'})
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No Current Assets Accounts Available</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#475569] uppercase tracking-wide mb-1">
                Destination Ledger
              </label>
              <select
                className={selectClass}
                value={formData.destination_ledger || ''}
                onChange={(e) => setFormData({ ...formData, destination_ledger: e.target.value })}
              >
                <option value="">Select Destination Ledger</option>
                {ledgerAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name.toUpperCase()} ({account.account_type ? account.account_type.toUpperCase() : 'N/A'})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FormInput
                label="Officer"
                name="officer_name"
                value={staffDetails.find(staff => staff.id === loginId)?.name ||
                  staffDetails.find(staff => staff.id === loginId)?.username ||
                  'UNKNOWN'}
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#475569] uppercase tracking-wide mb-1">
                Transaction Type <span className="text-danger-500">*</span>
              </label>
              <select
                className={selectClass}
                value={formData.type || ''}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="">Select Type</option>
                <option value="payment">💸 Payment</option>
                <option value="receipt">💰 Receipt</option>
              </select>
            </div>

            <div>
              <FormInput
                label="Amount (₹)"
                name="amount"
                type="number"
                step="0.01"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <FormInput
                label="Date"
                name="date"
                type="date"
                value={formData.date || getTodayDate()}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <FormInput
              label="Particulars"
              name="particulars"
              type="textarea"
              rows="3"
              value={formData.particulars || ''}
              onChange={(e) => setFormData({ ...formData, particulars: e.target.value })}
              placeholder="Enter transaction details..."
              required
            />
          </div>
        </form>
      </Modal>
    </PageLayout>
  );
};

export default BalanceBook;